/**
 * AfterPrayerSalahChangesOverlay
 *
 * Full-screen overlay that shows the upcoming salah change for a specific prayer
 * immediately after that prayer ends. Reuses SimpleUpcomingSalahChangesSlideshow
 * for identical look-and-feel.
 */

import React from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import { ScreenData } from "../../types/ScreenTypes";
import AfterPrayerSalahChangesTemplateOverlay, {
  AfterPrayerSalahChangesTemplate,
  AfterPrayerSalahChangesTemplateField,
} from "../elements/AfterPrayerSalahChangesTemplateOverlay";
import SimpleUpcomingSalahChangesSlideshow from "../elements/SimpleUpcomingSalahChangesSlideshow";

interface AfterPrayerSalahChangesOverlayProps {
  /** Which prayer triggered this overlay (e.g. "JamDhuhr") */
  activePrayer: string;
  /** Screen settings */
  screenData: ScreenData;
  /** Today's prayer times */
  prayerTimes: PrayerTimes;
  /** Tomorrow's prayer times */
  tomorrowPrayerTimes: PrayerTimes | null;
  /** 7-day timetable array for change detection */
  timetables: PrayerTimes[];
  /** How long to display (seconds) */
  duration: number;
  /** Called when the overlay's display duration expires */
  onComplete: () => void;
  /** Previous Friday's Jummah times */
  prevFridayJummah1: string | null;
  prevFridayJummah2: string | null;
  prevFridayJummah3: string | null;
  prevFridayJummah4: string | null;
}

function normalizeTemplateField(
  value: unknown,
  fallback: AfterPrayerSalahChangesTemplateField,
): AfterPrayerSalahChangesTemplateField {
  const candidate = (value ??
    {}) as Partial<AfterPrayerSalahChangesTemplateField>;
  const align =
    candidate.align === "left" ||
    candidate.align === "center" ||
    candidate.align === "right"
      ? candidate.align
      : fallback.align;

  return {
    x: Number.isFinite(Number(candidate.x)) ? Number(candidate.x) : fallback.x,
    y: Number.isFinite(Number(candidate.y)) ? Number(candidate.y) : fallback.y,
    width: Number.isFinite(Number(candidate.width))
      ? Number(candidate.width)
      : fallback.width,
    fontSize: Number.isFinite(Number(candidate.fontSize))
      ? Number(candidate.fontSize)
      : fallback.fontSize,
    fontWeight: Number.isFinite(Number(candidate.fontWeight))
      ? Number(candidate.fontWeight)
      : fallback.fontWeight,
    color: candidate.color || fallback.color,
    fontFamily: candidate.fontFamily || fallback.fontFamily,
    align,
  };
}

function parseAfterPrayerTemplate(
  templateJson: string | undefined,
): AfterPrayerSalahChangesTemplate | null {
  if (!templateJson || !templateJson.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(
      templateJson,
    ) as Partial<AfterPrayerSalahChangesTemplate>;
    const canvasWidth = Number(parsed.canvasWidth);
    const canvasHeight = Number(parsed.canvasHeight);

    if (
      !Number.isFinite(canvasWidth) ||
      !Number.isFinite(canvasHeight) ||
      canvasWidth <= 0 ||
      canvasHeight <= 0
    ) {
      return null;
    }

    const defaultNameField: AfterPrayerSalahChangesTemplateField = {
      x: canvasWidth / 2,
      y: canvasHeight * 0.34,
      width: canvasWidth * 0.72,
      fontSize: 88,
      fontWeight: 700,
      color: "#2f2018",
      fontFamily: "Arial, sans-serif",
      align: "center",
    };

    const defaultTimeField: AfterPrayerSalahChangesTemplateField = {
      x: canvasWidth / 2,
      y: canvasHeight * 0.61,
      width: canvasWidth * 0.72,
      fontSize: 96,
      fontWeight: 700,
      color: "#2f2018",
      fontFamily: "Arial, sans-serif",
      align: "center",
    };

    return {
      version: 1,
      canvasWidth,
      canvasHeight,
      prayerName: normalizeTemplateField(parsed.prayerName, defaultNameField),
      prayerTime: normalizeTemplateField(parsed.prayerTime, defaultTimeField),
    };
  } catch {
    return null;
  }
}

export default function AfterPrayerSalahChangesOverlay({
  activePrayer,
  screenData,
  prayerTimes,
  tomorrowPrayerTimes,
  timetables,
  duration,
  onComplete,
  prevFridayJummah1,
  prevFridayJummah2,
  prevFridayJummah3,
  prevFridayJummah4,
}: AfterPrayerSalahChangesOverlayProps) {
  const template = parseAfterPrayerTemplate(
    screenData.salahChangesAfterPrayerTemplateJson,
  );

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 9000,
        backgroundColor: "#000",
      }}
    >
      {template ? (
        <AfterPrayerSalahChangesTemplateOverlay
          activePrayer={activePrayer}
          screenData={screenData}
          tomorrowPrayerTimes={tomorrowPrayerTimes}
          duration={duration}
          onComplete={onComplete}
          template={template}
        />
      ) : (
        <SimpleUpcomingSalahChangesSlideshow
          timetables={timetables}
          customizedNames={screenData.customizedNames || {}}
          duration={duration}
          onComplete={onComplete}
          prayerTimes={prayerTimes}
          tomorrowPrayerTimes={tomorrowPrayerTimes}
          screenData={screenData}
          prevFridayJummah1={prevFridayJummah1}
          prevFridayJummah2={prevFridayJummah2}
          prevFridayJummah3={prevFridayJummah3}
          prevFridayJummah4={prevFridayJummah4}
          width={window.innerWidth}
          height={window.innerHeight}
          filterPrayer={activePrayer}
          hideWhenNoChanges={false}
        />
      )}
    </div>
  );
}
